import React from 'react';
import { useData } from '../lib/DataContext.jsx';
import { KpiCard, BandPill, ToolTile, SectionTitle, ScoreCell, DataBarCell, Chip, ProvenanceFooter, ProvenanceBadge } from '../components/ui/kits.jsx';
import { ProjectCode } from '../components/ui/ProjectCode.jsx';
import { HBars, StackedBandBar } from '../components/charts/charts.jsx';
import { ScoreDistribution } from '../components/charts/advanced.jsx';
import { fmt, fmtCr, fmtInt, TOOL_COLOR, CRIT_ORDER, CRIT_COLOR } from '../lib/format.js';

export default function Overview() {
  const { data, agg: A } = useData();
  if (!data || !A) return null;

  return (
    <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 18 }} data-testid="page-overview">
      {/* KPI row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
        <KpiCard
          testid="kpi-programme-overall"
          hero
          label="Programme Overall"
          value={fmt(A.po, 2)}
          sub={`Across ${A.nScored} scored projects`}
          band={A.poBand}
        />
        <KpiCard
          testid="kpi-total-projects"
          label="Total Projects"
          value={fmtInt(A.n)}
          sub={`HRDP ${A.hrdp} · FDP ${A.fdp}`}
        />
        <KpiCard
          testid="kpi-total-budget"
          label="Total Budget"
          value={fmtCr(A.totalBudget)}
          sub={`${A.budgetCov} of ${A.n} projects funded`}
        />
        <KpiCard
          testid="kpi-budget-project"
          label="Budget / Project"
          value={fmtCr(A.meanBudget)}
          sub={`Median ${fmtCr(A.medianBudget)}`}
        />
        <KpiCard
          testid="kpi-partners"
          label="Implementing Partners"
          value={fmtInt(A.partnersCount)}
          sub={`Across ${A.statesCount} states`}
        />
      </div>

      {/* Thematic tool tiles */}
      <div className="card" style={{ padding: 18 }}>
        <SectionTitle right={<ProvenanceBadge kind="live" />}>Thematic Tool Performance</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          {['NRM', 'Health', 'SDLE', 'POE'].map((t) => {
            const tile = A.tiles[t];
            return (
              <ToolTile
                key={t}
                testid={`tooltile-${t}`}
                label={tile.label}
                score={tile.score}
                band={tile.band}
                projects={tile.projects}
                color={TOOL_COLOR[t]}
              />
            );
          })}
        </div>
      </div>

      {/* HRDP vs FDP */}
      <div className="card" style={{ padding: 18 }}>
        <SectionTitle right={<ProvenanceBadge kind="live" note="7-criterion breakdown; 3 real + 4 modelled averaged" />}>HRDP vs FDP · per OECD criterion</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
          {CRIT_ORDER.map((c) => {
            const w = data.meta.weights[c];
            const H = A.hf[c]?.HRDP;
            const F = A.hf[c]?.FDP;
            return (
              <div key={c} style={{ padding: 10, borderRadius: 8, background: 'var(--surface-2)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: CRIT_COLOR[c] }}>{c}</div>
                  <div style={{ fontSize: 10.5, color: 'var(--ink-4)', fontWeight: 700 }}>weight {(w * 100).toFixed(0)}%</div>
                </div>
                <div style={{ marginTop: 6, display: 'flex', gap: 6, flexDirection: 'column' }}>
                  <MiniBar label="H" value={H} color="#042a54" />
                  <MiniBar label="F" value={F} color="#9b2d20" />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Budget Range */}
      <div className="card" style={{ padding: 18 }}>
        <SectionTitle right={<ProvenanceBadge kind="live" />}>Budget Range Breakdown</SectionTitle>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {A.budgetRange.map((r) => (
            <div key={r.range} style={{ display: 'grid', gridTemplateColumns: '110px 1fr 180px', alignItems: 'center', gap: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 700 }}>{r.range}</div>
              <DataBarCell value={r.avgScore} />
              <div style={{ textAlign: 'right', fontSize: 12, color: 'var(--ink-3)' }} className="tabular">
                {r.n} project{r.n === 1 ? '' : 's'} · {fmtCr(r.totalBudgetCr)}
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 10, fontSize: 11, color: 'var(--ink-4)' }}>
          Caveat: budget breakdown covers the {A.budgetCov} funded projects with reported ₹ values.
        </div>
      </div>

      {/* Regional performance */}
      <div className="card" style={{ padding: 18 }}>
        <SectionTitle right={
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <ProvenanceBadge kind="live" />
            {['Excellent', 'Good', 'Moderate', 'Poor'].map((b) => (
              <div key={b} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10.5, color: 'var(--ink-3)', fontWeight: 600 }}>
                <span style={{ width: 10, height: 10, background: `var(--band-${b.toLowerCase()})`, borderRadius: 3 }} />
                {b}
              </div>
            ))}
          </div>
        }>
          Performance Category by Region
        </SectionTitle>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {A.regionPerf.map((r) => (
            <div key={r.region} style={{ display: 'grid', gridTemplateColumns: '160px 1fr 90px', alignItems: 'center', gap: 12 }}>
              <div>
                <div style={{ fontSize: 12.5, fontWeight: 700 }}>{r.region}</div>
                <div style={{ fontSize: 10.5, color: 'var(--ink-4)' }}>{r.zone} · n={r.n}</div>
              </div>
              <StackedBandBar bands={r.bands} />
              <div className="tabular" style={{ textAlign: 'right', fontWeight: 700, color: 'var(--ink)' }}>
                {fmt(r.avgScore, 2)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Score distribution — where do the 186 projects sit? */}
      <div className="card" style={{ padding: 18 }}>
        <SectionTitle right={
          <div style={{ display: 'flex', gap: 10, fontSize: 10.5, color: 'var(--ink-3)', fontWeight: 600, alignItems: 'center' }}>
            <ProvenanceBadge kind="live" />
            {['Poor', 'Moderate', 'Good', 'Excellent'].map((b) => (
              <div key={b} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ width: 10, height: 10, background: `var(--band-${b.toLowerCase()})`, borderRadius: 2 }} />
                {b}
              </div>
            ))}
          </div>
        }>
          Programme score distribution · where the {A.nScored} projects sit
        </SectionTitle>
        <ScoreDistribution testid="score-distribution" projects={A.projectsSorted} />
        <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 4 }}>
          Bins of 0.25 · dashed lines separate band thresholds (2.6 · 3.6 · 4.6).
        </div>
      </div>

      {/* Top projects + State table side-by-side */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 14 }}>
        <div className="card" style={{ padding: 18 }}>
          <SectionTitle right={<ProvenanceBadge kind="live" />}>Top Performing Projects</SectionTitle>
          <table className="pv-table">
            <thead>
              <tr><th>#</th><th>Project</th><th>Type</th><th style={{ textAlign: 'right' }}>Score</th></tr>
            </thead>
            <tbody>
              {A.projectsSorted.slice(0, 6).map((p, i) => (
                <tr key={p.code}>
                  <td className="tabular" style={{ color: 'var(--ink-4)', fontWeight: 700 }}>{i + 1}</td>
                  <td>
                    <div style={{ fontWeight: 700, fontSize: 12.5 }}><ProjectCode code={p.code} /></div>
                    <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{p.partner || '—'} · {p.state || '—'}</div>
                  </td>
                  <td><Chip color={p.type === 'HRDP' ? '#042a54' : '#9b2d20'}>{p.type}</Chip></td>
                  <td style={{ textAlign: 'right' }}><ScoreCell score={p.overall} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="card" style={{ padding: 18 }}>
          <SectionTitle right={<ProvenanceBadge kind="live" />}>State-wise Performance</SectionTitle>
          <div className="scroll-y" style={{ maxHeight: 380 }}>
            <table className="pv-table">
              <thead>
                <tr>
                  <th>State</th><th>Lead Partner</th><th>Region</th>
                  <th style={{ textAlign: 'right' }}>Projects</th>
                  <th style={{ textAlign: 'right' }}>Budget</th>
                  <th>Score</th><th>Band</th>
                </tr>
              </thead>
              <tbody>
                {A.states.slice(0, 20).map((s) => (
                  <tr key={s.state}>
                    <td style={{ fontWeight: 700 }}>{s.state}</td>
                    <td style={{ fontSize: 11.5, color: 'var(--ink-2)' }}>
                      {s.partner || '—'}{s.partnersMore > 0 && <span style={{ color: 'var(--ink-4)' }}> +{s.partnersMore}</span>}
                    </td>
                    <td style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>{s.region} · {s.zone}</td>
                    <td className="tabular" style={{ textAlign: 'right' }}>{s.projects}</td>
                    <td className="tabular nowrap" style={{ textAlign: 'right' }}>{fmtCr(s.budgetCr)}</td>
                    <td><DataBarCell value={s.avgScore} /></td>
                    <td><BandPill band={s.band} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <ProvenanceFooter text="OECD criteria computed from 638,432 responses: Relevance, Efficiency, Sustainability. Coherence, Effectiveness, Impact and Branding are currently modelled placeholders; production wiring is a build_data.py-only change (spec §10)." />
    </div>
  );
}

function MiniBar({ label, value, color }) {
  const pct = value ? Math.max(0, Math.min(100, (value / 5) * 100)) : 0;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '14px 1fr 44px', alignItems: 'center', gap: 6 }}>
      <div style={{ fontSize: 10, fontWeight: 800, color }}>{label}</div>
      <div style={{ height: 6, background: 'var(--line-2)', borderRadius: 4, overflow: 'hidden' }}>
        <div className="anim-grow" style={{ width: `${pct}%`, height: '100%', background: color }} />
      </div>
      <div className="tabular" style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink)', textAlign: 'right' }}>
        {value == null ? '—' : fmt(value, 2)}
      </div>
    </div>
  );
}
